// Command Palette — global ⌘K / Ctrl-K launcher inspired by every
// productivity tool an SE already lives in (Linear, Raycast, Slack,
// VS Code). Mounted once at the App root; listens globally so any
// page can trigger it.
//
// What it does:
//   • ⌘K / Ctrl-K   → open
//   • g <key>       → "go to" shortcut (vim-style: g p = profiles, etc.)
//   • Esc           → close
//   • ↑/↓           → navigate
//   • Enter         → run the selected command
//
// What it deliberately does NOT do (yet):
//   • search packs / playbooks by name (would need a query layer)
//   • support actions outside of "navigate to X" / "open AI generator"
//
// Both are easy follow-ups; this scaffold just registers a `Command`
// type and a static command list so adding an action later is one push
// to the array.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useUiStore } from "@/lib/store/uiStore";

interface Command {
  id: string;
  label: string;
  hint?: string;
  // "g <key>" for navigation, or any free-text keyword for searching.
  shortcut?: string;
  group: "Navigate" | "Actions" | "Help";
  // Predicate gate for org/curator/admin-only commands.
  enabled?: (auth: ReturnType<typeof useAuth>) => boolean;
  run: (ctx: {
    navigate: ReturnType<typeof useNavigate>;
    setAiGenOpen: (v: boolean) => void;
  }) => void;
}

const COMMANDS: Command[] = [
  // Navigate ----------------------------------------------------------
  {
    id: "nav-build",
    label: "Go to Build",
    shortcut: "g b",
    group: "Navigate",
    run: ({ navigate }) => navigate("/"),
  },
  {
    id: "nav-runs",
    label: "Go to Runs",
    shortcut: "g r",
    group: "Navigate",
    enabled: (a) => a.orgConnected,
    run: ({ navigate }) => navigate("/runs"),
  },
  {
    id: "nav-profiles",
    label: "Go to Profiles",
    shortcut: "g p",
    group: "Navigate",
    enabled: (a) => a.orgConnected,
    run: ({ navigate }) => navigate("/profiles"),
  },
  {
    id: "nav-templates",
    label: "Go to Quick Builds",
    shortcut: "g t",
    group: "Navigate",
    enabled: (a) => a.orgConnected,
    run: ({ navigate }) => navigate("/templates"),
  },
  {
    id: "nav-explore",
    label: "Go to Explore",
    shortcut: "g e",
    group: "Navigate",
    enabled: (a) => a.orgConnected,
    run: ({ navigate }) => navigate("/explore"),
  },
  {
    id: "nav-playbooks",
    label: "Go to Playbooks",
    shortcut: "g k",
    group: "Navigate",
    enabled: (a) => a.orgConnected,
    run: ({ navigate }) => navigate("/playbooks"),
  },
  {
    id: "nav-coach",
    label: "Go to AI Coach",
    shortcut: "g c",
    group: "Navigate",
    enabled: (a) => a.orgConnected,
    run: ({ navigate }) => navigate("/coach"),
  },
  {
    id: "nav-docs",
    label: "Go to Docs",
    shortcut: "g d",
    group: "Navigate",
    run: ({ navigate }) => navigate("/docs"),
  },
  {
    id: "nav-curator",
    label: "Go to Curator console",
    group: "Navigate",
    enabled: (a) => a.isCurator,
    run: ({ navigate }) => navigate("/curator"),
  },
  {
    id: "nav-admin",
    label: "Go to Admin console",
    group: "Navigate",
    enabled: (a) => a.isAdmin,
    run: ({ navigate }) => navigate("/admin"),
  },
  // Actions -----------------------------------------------------------
  {
    id: "act-generate",
    label: "✦ Generate a new profile pack",
    hint: "Open the AI Profile Generator sheet",
    group: "Actions",
    enabled: (a) => a.orgConnected,
    run: ({ setAiGenOpen }) => setAiGenOpen(true),
  },
  {
    id: "act-launch-build",
    label: "Start a new build",
    hint: "Open Configure → pick a mode",
    group: "Actions",
    enabled: (a) => a.orgConnected,
    run: ({ navigate }) => navigate("/"),
  },
  // Help --------------------------------------------------------------
  {
    id: "help-docs",
    label: "Open Documentation",
    group: "Help",
    run: ({ navigate }) => navigate("/docs"),
  },
  {
    id: "help-shortcuts",
    label: "Show keyboard shortcuts",
    group: "Help",
    run: ({ navigate }) => navigate("/docs#shortcuts"),
  },
];

export function CommandPalette(): JSX.Element | null {
  const navigate = useNavigate();
  const auth = useAuth();
  const setAiGenOpen = useUiStore((s) => s.setAiGenOpen);

  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // "g <key>" sequence buffer — when the user types "g" outside of an
  // input field we wait briefly for the second key. This is the same
  // pattern Linear / GitHub use; it's discoverable via the docs page.
  const gSeq = useRef<{ active: boolean; timer?: number }>({ active: false });

  const close = useCallback((): void => {
    setOpen(false);
    setQ("");
    setCursor(0);
  }, []);

  // Filter + group commands.
  const visible = useMemo(() => {
    const enabled = COMMANDS.filter(
      (c) => !c.enabled || c.enabled(auth),
    );
    if (!q.trim()) return enabled;
    const needle = q.toLowerCase();
    return enabled.filter(
      (c) =>
        c.label.toLowerCase().includes(needle) ||
        (c.hint ?? "").toLowerCase().includes(needle) ||
        (c.shortcut ?? "").toLowerCase().includes(needle),
    );
  }, [q, auth]);

  // Reset cursor when results change so we don't point past the end.
  useEffect(() => {
    setCursor(0);
  }, [q, open]);

  // Keep the palette focused when it opens.
  useEffect(() => {
    if (open) {
      // microtask delay so the input exists before we focus.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  // Global key handlers — open on ⌘K/Ctrl-K, "?" for help, and the
  // two-step "g <key>" navigation. Skip when typing in inputs/textareas
  // so we don't steal keystrokes.
  useEffect(() => {
    function isTypingTarget(t: EventTarget | null): boolean {
      if (!(t instanceof HTMLElement)) return false;
      const tag = t.tagName;
      return (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        t.isContentEditable
      );
    }

    function runShortcutKey(key: string): boolean {
      const cmd = COMMANDS.find(
        (c) =>
          c.shortcut === `g ${key}` &&
          (!c.enabled || c.enabled(auth)),
      );
      if (cmd) {
        cmd.run({ navigate, setAiGenOpen });
        return true;
      }
      return false;
    }

    function onKey(e: KeyboardEvent): void {
      // ⌘K / Ctrl-K — always.
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
        return;
      }
      // "?" outside of inputs — open palette with help-ish state.
      if (e.key === "?" && !isTypingTarget(e.target)) {
        e.preventDefault();
        setOpen(true);
        setQ("");
        return;
      }
      // Esc closes.
      if (e.key === "Escape" && open) {
        e.preventDefault();
        close();
        return;
      }
      // "g <key>" two-step nav. Ignored while typing or while the
      // palette is open (Esc first if the user wants to use shortcuts).
      if (open || isTypingTarget(e.target)) return;
      if (e.key === "g" && !e.metaKey && !e.ctrlKey && !e.altKey) {
        gSeq.current.active = true;
        if (gSeq.current.timer) window.clearTimeout(gSeq.current.timer);
        gSeq.current.timer = window.setTimeout(() => {
          gSeq.current.active = false;
        }, 900);
        return;
      }
      if (gSeq.current.active && /^[a-z]$/.test(e.key)) {
        if (runShortcutKey(e.key)) {
          e.preventDefault();
          gSeq.current.active = false;
          if (gSeq.current.timer) window.clearTimeout(gSeq.current.timer);
        }
      }
    }

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, auth, navigate, setAiGenOpen, close]);

  if (!open) return null;

  function moveCursor(delta: number): void {
    setCursor((c) => {
      const next = c + delta;
      if (next < 0) return Math.max(visible.length - 1, 0);
      if (next >= visible.length) return 0;
      return next;
    });
  }

  function activate(cmd: Command): void {
    cmd.run({ navigate, setAiGenOpen });
    close();
  }

  return (
    <div
      role="dialog"
      aria-label="Command palette"
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(3, 45, 96, 0.32)",
        backdropFilter: "blur(2px)",
        zIndex: 200,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        paddingTop: "12vh",
      }}
    >
      <div
        style={{
          width: "min(560px, 92vw)",
          background: "white",
          borderRadius: 12,
          boxShadow: "0 24px 60px -16px rgba(3,45,96,.45)",
          border: "1px solid var(--bd)",
          overflow: "hidden",
        }}
      >
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              moveCursor(1);
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              moveCursor(-1);
            } else if (e.key === "Enter") {
              const cmd = visible[cursor];
              if (cmd) {
                e.preventDefault();
                activate(cmd);
              }
            }
          }}
          placeholder="Type a command or page…"
          style={{
            width: "100%",
            padding: "14px 18px",
            fontSize: 14,
            border: 0,
            outline: "none",
            color: "var(--ink)",
            background: "transparent",
            borderBottom: "1px solid var(--bd-2)",
          }}
        />
        <div
          style={{
            maxHeight: "50vh",
            overflowY: "auto",
            padding: "6px 0",
          }}
          className="thin-scroll"
        >
          {visible.length === 0 && (
            <div
              style={{
                padding: "24px 18px",
                color: "var(--ink-3)",
                fontSize: 12,
                textAlign: "center",
              }}
            >
              No commands match.
            </div>
          )}
          {renderGrouped(visible, cursor, activate)}
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "8px 14px",
            background: "var(--cb-98)",
            borderTop: "1px solid var(--bd-2)",
            fontSize: 11,
            color: "var(--ink-3)",
          }}
        >
          <div style={{ display: "flex", gap: 10 }}>
            <span>
              <span className="kbd">↑</span> <span className="kbd">↓</span>{" "}
              navigate
            </span>
            <span>
              <span className="kbd">↵</span> select
            </span>
            <span>
              <span className="kbd">Esc</span> close
            </span>
          </div>
          <span>
            <span className="kbd">g</span> + key for fast nav
          </span>
        </div>
      </div>
    </div>
  );
}

function renderGrouped(
  commands: Command[],
  cursor: number,
  activate: (cmd: Command) => void,
): JSX.Element[] {
  const groups: Record<string, Command[]> = {};
  commands.forEach((c) => {
    (groups[c.group] ||= []).push(c);
  });
  const out: JSX.Element[] = [];
  let runningIndex = 0;
  (["Navigate", "Actions", "Help"] as const).forEach((g) => {
    const items = groups[g];
    if (!items?.length) return;
    out.push(
      <div
        key={`h-${g}`}
        className="label"
        style={{ padding: "8px 18px 4px" }}
      >
        {g}
      </div>,
    );
    items.forEach((c) => {
      const myIndex = runningIndex++;
      const active = myIndex === cursor;
      out.push(
        <button
          key={c.id}
          onMouseEnter={() => {
            /* leave cursor alone — user is typing, not pointing */
          }}
          onClick={() => activate(c)}
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "8px 18px",
            background: active ? "var(--cb-95)" : "transparent",
            border: 0,
            textAlign: "left",
            cursor: "pointer",
            fontSize: 13,
            color: "var(--ink)",
          }}
        >
          <span style={{ flex: 1 }}>
            {c.label}
            {c.hint && (
              <span
                style={{
                  display: "block",
                  fontSize: 11,
                  color: "var(--ink-3)",
                  marginTop: 2,
                }}
              >
                {c.hint}
              </span>
            )}
          </span>
          {c.shortcut && (
            <span style={{ display: "inline-flex", gap: 3 }}>
              {c.shortcut.split(" ").map((part, i) => (
                <span key={i} className="kbd">
                  {part}
                </span>
              ))}
            </span>
          )}
        </button>,
      );
    });
  });
  return out;
}
